import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router';
import {
    Plus,
    Edit01,
    Trash01,
    Copy01
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { useDocumentSchema } from '@/hooks/api/use-document-schemas';
import {
    useSchemaRules,
    useCreateSchemaRule,
    useUpdateSchemaRule,
    useDeleteSchemaRule,
    useDuplicateSchemaRule,
} from '@/hooks/api/use-schema-rules';
import type {
    DocumentSchemaRuleResponse,
    DocumentSchemaRuleCreate,
    DocumentSchemaRuleUpdate,
} from '@/types/api/document-schemas';
import { useTranslation } from 'react-i18next';

export const ParserRulesPage = () => {
    const { documentTypeId, schemaId } = useParams<{ documentTypeId: string; schemaId: string }>();
    const { t } = useTranslation(['pages:documents-admin', 'common']);

    // API Hooks
    const { data: schema, isLoading: isLoadingSchema, error: schemaError } = useDocumentSchema(schemaId || '');
    const { data: rules = [], isLoading: isLoadingRules, error: rulesError } = useSchemaRules(schemaId || '');

    const createRuleMutation = useCreateSchemaRule();
    const updateRuleMutation = useUpdateSchemaRule();
    const deleteRuleMutation = useDeleteSchemaRule();
    const duplicateRuleMutation = useDuplicateSchemaRule();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<DocumentSchemaRuleResponse | null>(null);
    const [isDuplicateConfirmOpen, setIsDuplicateConfirmOpen] = useState(false);
    const [ruleToDuplicate, setRuleToDuplicate] = useState<DocumentSchemaRuleResponse | null>(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [ruleToDelete, setRuleToDelete] = useState<DocumentSchemaRuleResponse | null>(null);

    const [ruleFormData, setRuleFormData] = useState<DocumentSchemaRuleCreate>({
        name: '',
        document_type: schema?.document_type || '',
        prompt: '',
        description: '',
        priority: 0,
        is_active: true,
    });

    const isLoading = isLoadingSchema || isLoadingRules;
    const error = schemaError || rulesError;

    // Update form data when schema loads (only if not editing)
    useEffect(() => {
        if (schema && !editingRule && !isModalOpen) {
            setRuleFormData((prev) => ({
                ...prev,
                document_type: schema.document_type,
            }));
        }
    }, [schema, editingRule, isModalOpen]);

    const handleOpenModal = (rule?: DocumentSchemaRuleResponse) => {
        if (rule) {
            setEditingRule(rule);
            setRuleFormData({
                name: rule.name,
                document_type: rule.document_type,
                prompt: rule.prompt,
                description: rule.description || '',
                priority: rule.priority,
                is_active: rule.is_active,
            });
        } else {
            setEditingRule(null);
            setRuleFormData({
                name: '',
                document_type: schema?.document_type || '',
                prompt: '',
                description: '',
                priority: 0,
                is_active: true,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingRule(null);
    };

    const handleSave = async () => {
        if (!schemaId || !schema) return;

        try {
            if (editingRule) {
                const updateData: DocumentSchemaRuleUpdate = {
                    name: ruleFormData.name,
                    prompt: ruleFormData.prompt,
                    description: ruleFormData.description || null,
                    priority: ruleFormData.priority,
                    is_active: ruleFormData.is_active,
                };
                await updateRuleMutation.mutateAsync({
                    schemaId,
                    ruleId: editingRule.document_schema_rule_id,
                    data: updateData,
                });
            } else {
                // Ensure document_type is set from schema
                const createData: DocumentSchemaRuleCreate = {
                    ...ruleFormData,
                    document_type: schema.document_type,
                };
                await createRuleMutation.mutateAsync({
                    schemaId,
                    data: createData,
                });
            }
            handleCloseModal();
        } catch (error) {
            console.error('Error saving rule:', error);
            // Error handling is done by React Query
        }
    };

    const handleDeleteClick = (rule: DocumentSchemaRuleResponse) => {
        setRuleToDelete(rule);
        setIsDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!schemaId || !ruleToDelete) return;

        try {
            await deleteRuleMutation.mutateAsync({
                schemaId,
                ruleId: ruleToDelete.document_schema_rule_id,
            });
            setIsDeleteConfirmOpen(false);
            setRuleToDelete(null);
        } catch (error) {
            console.error('Error deleting rule:', error);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteConfirmOpen(false);
        setRuleToDelete(null);
    };

    const handleDuplicateClick = (rule: DocumentSchemaRuleResponse) => {
        setRuleToDuplicate(rule);
        setIsDuplicateConfirmOpen(true);
    };

    const handleConfirmDuplicate = async () => {
        if (!schemaId || !ruleToDuplicate) return;

        try {
            await duplicateRuleMutation.mutateAsync({
                schemaId,
                ruleId: ruleToDuplicate.document_schema_rule_id,
            });
            setIsDuplicateConfirmOpen(false);
            setRuleToDuplicate(null);
        } catch (error) {
            console.error('Error duplicating rule:', error);
        }
    };

    const handleCancelDuplicate = () => {
        setIsDuplicateConfirmOpen(false);
        setRuleToDuplicate(null);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !schema) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-error">{t('error.loading') || 'Error loading data'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm">
                <Link
                    to="/admin/documents/types"
                    className="text-tertiary hover:text-primary"
                >
                    {t('breadcrumb.documentTypes')}
                </Link>
                <span className="text-tertiary">/</span>
                <Link
                    to={`/admin/documents/types/${documentTypeId}/schemas`}
                    className="text-tertiary hover:text-primary"
                >
                    {documentTypeId}
                </Link>
                <span className="text-tertiary">/</span>
                <Link
                    to={`/admin/documents/types/${documentTypeId}/schemas`}
                    className="text-tertiary hover:text-primary"
                >
                    {t('breadcrumb.schemas')}
                </Link>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{schema.schema_name}</span>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{t('parserRules.title')}</span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">
                        {t('parserRules.title')} - {schema.schema_name}
                    </h2>
                    <p className="text-tertiary mt-1">{t('parserRules.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('parserRules.actions.add')}
                </Button>
            </div>

            {/* Rules Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('parserRules.title')} (${rules.length})`}
                    description={t('parserRules.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('parserRules.modal.fields.ruleName')} isRowHeader />
                        <Table.Head label={t('parserRules.modal.fields.naturalLanguageRule')} />
                        <Table.Head label={t('parserRules.priority')} />
                        <Table.Head label={t('parserRules.modal.fields.isActive')} />
                        <Table.Head label={t('parserRules.modal.fields.description')} />
                        <Table.Head label={t('schemas.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {rules.map((rule) => (
                            <Table.Row key={rule.document_schema_rule_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{rule.name}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="text-sm text-primary max-w-md">{rule.prompt}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color="gray" size="sm">{rule.priority}</Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    {rule.is_active ? (
                                        <Badge color="success" size="sm">{t('parserRules.status.active')}</Badge>
                                    ) : (
                                        <Badge color="error" size="sm">{t('parserRules.status.inactive')}</Badge>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{rule.description || '-'}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => handleOpenModal(rule)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                            disabled={updateRuleMutation.isPending}
                                        >
                                            {t('parserRules.actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDuplicateClick(rule)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Copy01}
                                            disabled={duplicateRuleMutation.isPending}
                                        >
                                            {t('parserRules.actions.duplicate') || 'Duplicate'}
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteClick(rule)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                            disabled={deleteRuleMutation.isPending}
                                        >
                                            {t('parserRules.actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {rules.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('parserRules.modal.noRules')}</p>
                    </div>
                </div>
            )}

            {/* Modal for Add/Edit Rule */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingRule ? t('parserRules.modal.editRule') : t('parserRules.modal.addRule')}
                                </h2>

                                <div className="space-y-4">
                                    <Input
                                        label={t('parserRules.modal.fields.ruleName')}
                                        placeholder={t('parserRules.modal.fields.ruleNamePlaceholder')}
                                        value={ruleFormData.name}
                                        onChange={(value) => setRuleFormData({ ...ruleFormData, name: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('parserRules.modal.fields.naturalLanguageRule')}
                                        </label>
                                        <textarea
                                            className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary placeholder-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                            rows={8}
                                            value={ruleFormData.prompt}
                                            onChange={(e) => setRuleFormData({ ...ruleFormData, prompt: e.target.value })}
                                            placeholder={t('parserRules.modal.fields.naturalLanguageRulePlaceholder')}
                                        />
                                        <p className="text-xs text-tertiary mt-1">
                                            {t('parserRules.modal.fields.naturalLanguageRuleHelp')}
                                        </p>
                                    </div>

                                    <Input
                                        label={t('parserRules.modal.fields.description')}
                                        placeholder={t('parserRules.modal.fields.descriptionPlaceholder')}
                                        value={ruleFormData.description || ''}
                                        onChange={(value) => setRuleFormData({ ...ruleFormData, description: value })}
                                        size="md"
                                    />

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('parserRules.modal.fields.priority')}
                                            value={String(ruleFormData.priority || 0)}
                                            onChange={(value) => setRuleFormData({ ...ruleFormData, priority: Number(value) || 0 })}
                                            size="md"
                                        />

                                        <div className="flex items-center gap-2 pt-6">
                                            <input
                                                type="checkbox"
                                                checked={ruleFormData.is_active !== undefined ? ruleFormData.is_active : true}
                                                onChange={(e) => setRuleFormData({ ...ruleFormData, is_active: e.target.checked })}
                                                className="rounded border-secondary"
                                            />
                                            <label className="text-sm text-primary">{t('parserRules.modal.fields.isActive')}</label>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button
                                        onClick={handleCloseModal}
                                        size="md"
                                        color="secondary"
                                        disabled={createRuleMutation.isPending || updateRuleMutation.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleSave}
                                        size="md"
                                        color="primary"
                                        disabled={createRuleMutation.isPending || updateRuleMutation.isPending}
                                    >
                                        {createRuleMutation.isPending || updateRuleMutation.isPending
                                            ? t('loading') || 'Loading...'
                                            : editingRule
                                                ? t('modal.save')
                                                : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Modal for Duplicate Confirmation */}
            <DialogTrigger isOpen={isDuplicateConfirmOpen} onOpenChange={(open) => !open && handleCancelDuplicate()}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('parserRules.actions.confirmDuplicate.title') || 'Duplicate Rule'}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {ruleToDuplicate
                                        ? t('parserRules.actions.confirmDuplicate.message', { name: ruleToDuplicate.name }) ||
                                        `Are you sure you want to duplicate the rule "${ruleToDuplicate.name}"? A new rule will be created with the same configuration.`
                                        : ''}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={handleCancelDuplicate}
                                        size="md"
                                        color="secondary"
                                        disabled={duplicateRuleMutation.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleConfirmDuplicate}
                                        size="md"
                                        color="primary"
                                        disabled={duplicateRuleMutation.isPending}
                                    >
                                        {duplicateRuleMutation.isPending
                                            ? t('loading') || 'Loading...'
                                            : t('parserRules.actions.duplicate') || 'Duplicate'}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Modal for Delete Confirmation */}
            <DialogTrigger isOpen={isDeleteConfirmOpen} onOpenChange={(open) => !open && handleCancelDelete()}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('parserRules.actions.confirmDelete.title') || t('modal.confirmDelete') || 'Confirm Deletion'}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {ruleToDelete
                                        ? t('parserRules.actions.confirmDelete.message', { name: ruleToDelete.name }) ||
                                        t('modal.confirmDeleteMessage') ||
                                        `Are you sure you want to delete the rule "${ruleToDelete.name}"? This action cannot be undone.`
                                        : ''}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={handleCancelDelete}
                                        size="md"
                                        color="secondary"
                                        disabled={deleteRuleMutation.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleConfirmDelete}
                                        size="md"
                                        color="secondary-destructive"
                                        disabled={deleteRuleMutation.isPending}
                                    >
                                        {deleteRuleMutation.isPending
                                            ? t('loading') || 'Loading...'
                                            : t('parserRules.actions.delete') || 'Delete'}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
